






























































































































































import { Component, Watch, Prop } from "vue-property-decorator";
import { CloudflareplayerConfiguration } from "./cloudflareplayerConfiguration";
import WidgetBase from "../WidgetBase.vue";
import VuePlyr from "vue-plyr";
import { PlyrRef } from "../../../interfaces/plyrRef";
import CloudflareplayerUploadScreen from "./CloudflareplayerUploadScreen.vue";
import { CloudFlareSource } from "../../../interfaces/plyrSources";
import { CloudflareplayerState } from "./cloudflareplayerState";
import { BitrateInfo, MediaPlayer, MediaPlayerSettingClass } from "dashjs";
import { FileService } from "@/services/fileService";
import Hls, { Level } from "hls.js";
import { ErrorReportService } from "@/services/errorReportService";

@Component({ components: { VuePlyr, CloudflareplayerUploadScreen } })
export default class CloudflareplayerWidget extends WidgetBase<
    CloudflareplayerConfiguration
> {
    @Prop() widgetHeight!: number;

    imgWidth: number = 400;
    imgHeight: number = 400;
    volume: number = 0;
    muted: boolean = false;
    // only for local state change e.g. customer wants to stop
    plyrPaused: boolean = true;
    dash: dashjs.MediaPlayerClass | null = null;
    hls: Hls | null = null;
    selectedLevel: BitrateInfo | Level | null = null;
    levels: BitrateInfo[] | Level[] = [];
    levelSelectLabel: string = "Auto";
    videoLoading: boolean = true;
    latestServerTime: number = 0;
    browserIsBlockingVideo: boolean = false;
    overlay: boolean = false;
    absolute: boolean = true;

    public async mounted() {
        this.browserIsBlockingVideo = await FileService.checkIfBrowserIsBlockingVideo();
        if (this.browserIsBlockingVideo) {
            try {
                await ErrorReportService.createReport(
                    { payload: "Blocker aktiv, Warnung angezeigt" },
                    this.$store.state.user || null
                );
            } catch (error) {
                console.log(error);
            }
        }
    }

    public static get thumbnailAsset() {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        return require("@/assets/widget-thumbnails/video-player.svg");
    }

    public image(name: string) {
        return require(`@/assets/widget-thumbnails/${name}`);
    }
    public get isPresenter(): boolean {
        if (!this.$store.state.studio && this.$store.state.user) {
            return true;
        } else {
            return this.$store.getters.isPresenter;
        }
    }
    public static get type() {
        return "cloudflareplayer";
    }

    public $refs!: {
        plyr: Vue & PlyrRef;
        videoElement: HTMLVideoElement;
    };

    public get source(): CloudFlareSource {
        return this.config.state?.cloudflareSource || "";
    }
    public set source(newSource: CloudFlareSource) {
        this.update({
            config: this.config,
            payload: {
                cloudflareSource: newSource,
            },
        });
    }

    public get plyrOptions() {
        if (this.isPresenter) {
            const controls = [
                "progress",
                "current-time",
                "mute",
                "volume",
                "captions",
                "settings",
            ];
            if (this.source.allowFullscreen) {
                controls.push("fullscreen");
            }
            return {
                controls: controls,
                keyboard: { focused: false, global: false },
                clickToPlay: false,
                settings: ["captions", "quality"],
                fullscreen: {
                    enabled: this.source.allowFullscreen,
                },
            };
        } else {
            const controls = ["volume", "mute", "settings"];
            if (this.source.allowFullscreen) {
                controls.push("fullscreen");
            }
            return {
                controls: controls,
                clickToPlay: false,
                settings: ["captions", "quality"],
                keyboard: { focused: false, global: false },
                fullscreen: {
                    enabled: this.source.allowFullscreen,
                },
            };
        }
    }
    @Watch("selectedLevel")
    public onLevelChange(newLevel: BitrateInfo | Level) {
        // if no new level is set, return to automatic
        if (!newLevel && this.hls) {
            this.hls.currentLevel = -1;
            return;
        }
        if (!newLevel && this.dash) {
            this.changeDashSettings({
                streaming: {
                    abr: { autoSwitchBitrate: { video: true, audio: true } },
                },
            });
            return;
        }

        const originalLevels = [...this.levels].reverse();
        const l = originalLevels.find(l => l.height == newLevel.height);

        if (l && this.hls) {
            const newLevelIndex = originalLevels.indexOf(l) || 0;
            this.hls.currentLevel = newLevelIndex;
        }

        if (l && this.dash) {
            this.changeDashSettings({
                streaming: {
                    abr: { autoSwitchBitrate: { video: false, audio: false } },
                },
            });
            const newLevelIndex = originalLevels.indexOf(l) || 0;
            this.dash.setQualityFor("video", newLevelIndex);
        }
    }

    public supportsDash() {
        // will be natively supported in dashjs 3.3.0
        // https://github.com/Dash-Industry-Forum/dash.js/pull/3497/files

        const hasWebKit = "WebKitMediaSource" in window;
        const hasMediaSource = "MediaSource" in window;

        return hasWebKit || hasMediaSource;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public changeDashSettings(newSettings: any) {
        if (this.dash) {
            const currentConfig: MediaPlayerSettingClass = this.dash.getSettings();
            const mySettings: MediaPlayerSettingClass = {
                debug: { ...currentConfig.debug, ...newSettings.debug },
                streaming: {
                    ...currentConfig.streaming,
                    ...newSettings.streaming,
                },
            };

            // console.log("newSettings", mySettings);
            this.dash.updateSettings(mySettings);
        }
    }
    public async plyrMounted() {
        let baseUri: string = "/api/file/cloudflare_watch_stream";
        const preFlightCheck = await FileService.proxyCheck(
            `${baseUri}/${this.source.uid}/mpd`
        );
        if (preFlightCheck != 200) {
            baseUri = "/api/file/cloudflare_watch_stream_behind_proxy";
            this.$store.commit("showSnackbar", {
                text: this.$t("widget.plyr.firewalled") as string,
                color: "warning",
            });
        }
        if (this.supportsDash()) {
            console.log("using dash");
            this.dash = MediaPlayer().create();
            this.dash.setAutoPlay(false);
            this.changeDashSettings({
                debug: { logLevel: 0 },
                streaming: {
                    jumpGaps: true,
                    smallGapLimit: 0.3,
                    reuseExistingSourceBuffers: false,
                    trackSwitchMode: {
                        audio: "neverReplace",
                        video: "neverReplace",
                    },
                    abr: { autoSwitchBitrate: { video: true, audio: true } },
                    flushBufferAtTrackSwitch: true,
                    lowLatencyEnabled: true,
                },
            });
            const video = this.$refs.videoElement;
            this.dash.on("canPlay", () => {
                if (this.dash && this.videoLoading) {
                    this.levels = [
                        ...this.dash.getBitrateInfoListFor("video"),
                    ].reverse();
                    this.whatTimeIsIt();
                    this.videoLoading = false;
                }
            });
            this.dash.on(
                "qualityChangeRendered",
                (e: {
                    mediaType: string;
                    oldQuality: number;
                    newQuality: number;
                }) => {
                    if (e.mediaType === "video") {
                        this.levelSelectLabel = `Auto (${
                            this.dash?.getBitrateInfoListFor("video")[
                                e.newQuality
                            ].height
                        }p)`;
                    }
                }
            );
            this.dash.initialize(
                video,
                `${baseUri}/${this.source.uid}/mpd`,
                false
            );
            return;
        }
        if (Hls.isSupported()) {
            console.log("using hls");
            const hls = new Hls();
            this.hls = hls;
            hls.attachMedia(this.$refs.videoElement);
            // https://github.com/video-dev/hls.js/blob/master/docs/API.md#runtime-events
            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                hls.loadSource(`${baseUri}/${this.source.uid}/m3u8`);
            });
            hls.on(
                Hls.Events.MANIFEST_PARSED,
                (event, data: { levels: Level[] }) => {
                    this.levels = [...data.levels].reverse();
                    this.whatTimeIsIt();
                    this.videoLoading = false;
                }
            );
            hls.on(
                Hls.Events.LEVEL_SWITCHED,
                (event, data: { level: number }) => {
                    this.levelSelectLabel = `Auto (${
                        this.hls?.levels[data.level].height
                    }p)`;
                }
            );
            hls.on(Hls.Events.ERROR, function(event, data) {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            // try to recover network error
                            hls.startLoad();
                            console.log("network error");
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            console.log("media error");
                            hls.recoverMediaError();
                            break;
                        default:
                            console.log("fatal error");
                            // cannot recover
                            hls.destroy();
                            break;
                    }
                }
            });
            return;
        } else if (
            this.$refs.videoElement.canPlayType("application/vnd.apple.mpegurl")
        ) {
            this.$refs.videoElement.src = `${baseUri}/${this.source.uid}/m3u8`;
            this.$refs.videoElement.muted = true;
            this.$refs.videoElement.addEventListener("loadedmetadata", () => {
                if (this.videoLoading) {
                    this.whatTimeIsIt();
                    this.videoLoading = false;
                }
            });
        }
    }

    public uploadDone(data: { source: CloudFlareSource }) {
        this.source = data.source;
    }

    public plyrReady() {
        this.volume = this.$refs.plyr.player.volume;
        this.muted = this.$refs.plyr.player.muted;
        if (this.source.allowAutoplay) {
            ["play", "pause", "seeked", "seeking"].forEach(e => {
                this.$refs.plyr.player.off(e, () => {
                    return;
                });
            });

            this.startPlayback();
            this.plyrPaused = false;
            this.config.state.videoRunning = true;
            return;
        }
        if (this.isPresenter) {
            this.$refs.plyr.player.on("play", () => {
                this.update({
                    config: this.config,
                    payload: {
                        videoRunning: true,
                        videoTimestamp: this.$refs.plyr.player.currentTime,
                    },
                });
            });
            this.$refs.plyr.player.on("pause", () => {
                this.update({
                    config: this.config,
                    payload: {
                        videoRunning: false,
                        videoTimestamp: this.$refs.plyr.player.currentTime,
                    },
                });
            });
            this.$refs.plyr.player.on("seeking", () => {
                this.videoLoading = true;
            });
            this.$refs.plyr.player.on("seeked", () => {
                this.videoLoading = false;
            });
        }
        this.$refs.plyr.player.on("volumechange", () => {
            this.volume = this.$refs.plyr.player.volume;
            this.muted = this.$refs.plyr.player.muted;
        });
    }

    public whatTimeIsIt() {
        if (!this.source.allowAutoplay) {
            this.update({
                config: this.config,
                payload: {
                    whatTimeIsIt: 0,
                },
            });
        }
    }

    @Watch("config.state")
    public watchstate(newState: CloudflareplayerState) {
        if (this.source.allowAutoplay) {
            return;
        }
        if (
            newState.serverTime > this.latestServerTime &&
            Math.abs(newState.serverTime - this.latestServerTime) > 0.05
        ) {
            this.timeChanger(newState.whatTimeIsIt);
        }
        this.latestServerTime = newState.serverTime;
    }

    public timeChanger(newTime: number) {
        if (newTime && this.$refs.plyr) {
            if (
                this.$refs.plyr.player &&
                newTime >= this.$refs.plyr.player.duration
            ) {
                // video is over, reset it
                this.update({
                    config: this.config,
                    payload: {
                        videoRunning: false,
                        videoTimestamp: 0,
                    },
                });
                return;
            }
            if (Math.abs(newTime - this.$refs.plyr.player.currentTime) < 1) {
                // ignore minor changes, such as the backend +0.01 for smoother scrolling
            } else {
                this.$refs.plyr.player.currentTime = newTime;
            }
            if (this.config.state.videoRunning) {
                this.startPlayback();
            }
            if (!this.config.state.videoRunning) {
                this.stopPlayback();
            }
        }
    }

    public startPlayback() {
        this.$refs.videoElement.play();
        this.plyrPaused = false;
        if (this.source.allowAutoplay) {
            this.config.state.videoRunning = true;
        }
    }

    public stopPlayback() {
        this.$refs.plyr.player.pause();
        this.plyrPaused = true;
        if (this.source.allowAutoplay && this.isPresenter) {
            this.config.state.videoRunning = false;
        }
    }

    public rewind() {
        this.$refs.plyr.player.currentTime = 0;
        this.update({
            config: this.config,
            payload: {
                videoRunning: false,
                videoTimestamp: 0,
            },
        });
    }

    public beforeDestroy() {
        this.hls?.destroy();
        this.dash?.reset();
        if (this.$refs.plyr) {
            this.$refs.plyr.player.destroy();
        }
    }
}
